import { Badge, Menu } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightDots,
  faCirclePlay,
  faClipboardList,
  faComments,
  faFileContract,
  faFileInvoiceDollar,
  faGaugeHigh,
  faHospital,
  faLayerGroup,
  faListUl,
  faUserGroup,
  faUserShield,
} from '@fortawesome/free-solid-svg-icons';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { getVendorLogoPublicUrl } from '../../utilities/vendorLogoUrl';
import { hasPermission, isAuthenticated } from '../../utilities/authentication';
import useTranslation from '../../localization/useTranslation';
import http from '../../store/services/http-common';
import { SUPPORT_UNREAD_CHANGED_EVENT } from '../../utilities/supportInbox';
import platformLogoLight from '../../images/logo/Dentris-logo-light.png';
import platformLogoDark from '../../images/logo/Dentrist-logo-dark.png';
import './SideBar.css';

const NavIcon = ({ icon }) => (
  <span className="sidebar-nav-icon" aria-hidden="true">
    <FontAwesomeIcon icon={icon} />
  </span>
);

const SideBar = () => {
  const { theme } = useSelector((state) => state.theme);
  const { settings } = useSelector((state) => state.settings);
  const location = useLocation();
  const { t } = useTranslation();
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingPlanRequests, setPendingPlanRequests] = useState(0);
  const [supportUnread, setSupportUnread] = useState(0);
  const isDark = theme === 'dark';

  const vendorLogoUrl = useMemo(() => getVendorLogoPublicUrl(settings?.logo_path), [settings?.logo_path]);
  const brandLogoUrl = vendorLogoUrl || (isDark ? platformLogoDark : platformLogoLight);
  const brandAlt = settings?.name || t('auth.login.backofficeTitle') || 'Dentrist';

  useEffect(() => {
    if (!isAuthenticated()) return;
    http.get('/backoffice/dashboard')
      .then((res) => {
        const kpis = res?.data?.data?.kpis;
        setPendingCount(Number(kpis?.pending_subscriptions ?? 0));
        setPendingPlanRequests(Number(kpis?.pending_plan_requests ?? 0));
      })
      .catch(() => {
        setPendingCount(0);
        setPendingPlanRequests(0);
      });
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated()) return undefined;
    const loadSupportUnread = () => {
      http.get('/backoffice/support/unread-count')
        .then((res) => setSupportUnread(Number(res?.data?.data?.unread_conversations ?? 0)))
        .catch(() => {});
    };
    loadSupportUnread();
    const timer = setInterval(() => {
      if (!document.hidden) loadSupportUnread();
    }, 30000);
    window.addEventListener(SUPPORT_UNREAD_CHANGED_EVENT, loadSupportUnread);
    return () => {
      clearInterval(timer);
      window.removeEventListener(SUPPORT_UNREAD_CHANGED_EVENT, loadSupportUnread);
    };
  }, []);

  const items = useMemo(() => {
    const nav = [
      {
        key: 'dashboard',
        icon: <NavIcon icon={faGaugeHigh} />,
        label: <Link to="/dashboard">{t('sidebar.dashboard')}</Link>,
      },
      {
        key: 'backoffice_vendors',
        icon: <NavIcon icon={faHospital} />,
        label: <Link to="/vendors">{t('backoffice.vendors.title')}</Link>,
      },
      {
        key: 'subscription_plans',
        icon: <NavIcon icon={faLayerGroup} />,
        label: <Link to="/subscription-plans">{t('backoffice.subscriptionPlans.title')}</Link>,
      },
      {
        key: 'subscriptions',
        icon: <NavIcon icon={faFileContract} />,
        label: (
          <Link to="/subscriptions">
            {pendingCount > 0 ? (
              <Badge count={pendingCount} size="small" offset={[8, 0]}>
                {t('backoffice.subscriptions.title')}
              </Badge>
            ) : (
              t('backoffice.subscriptions.title')
            )}
          </Link>
        ),
      },
      {
        key: 'plan_requests',
        icon: <NavIcon icon={faArrowUpRightDots} />,
        label: (
          <Link to="/plan-requests">
            {pendingPlanRequests > 0 ? (
              <Badge count={pendingPlanRequests} size="small" offset={[8, 0]}>
                {t('backoffice.planRequests.title')}
              </Badge>
            ) : (
              t('backoffice.planRequests.title')
            )}
          </Link>
        ),
      },
      {
        key: 'demo_requests',
        icon: <NavIcon icon={faCirclePlay} />,
        label: <Link to="/demo-requests">{t('backoffice.demoRequests.title')}</Link>,
      },
      {
        key: 'support',
        icon: <NavIcon icon={faComments} />,
        label: (
          <Link to="/support">
            {supportUnread > 0 ? (
              <Badge count={supportUnread} size="small" offset={[8, 0]}>
                {t('backoffice.support.title')}
              </Badge>
            ) : (
              t('backoffice.support.title')
            )}
          </Link>
        ),
      },
      {
        key: 'platform_clients',
        icon: <NavIcon icon={faUserGroup} />,
        label: <Link to="/clients">{t('sidebar.platform_clients')}</Link>,
      },
      {
        key: 'platform_orders',
        icon: <NavIcon icon={faClipboardList} />,
        label: <Link to="/orders">{t('sidebar.platform_orders')}</Link>,
      },
      {
        key: 'platform_invoices',
        icon: <NavIcon icon={faFileInvoiceDollar} />,
        label: <Link to="/invoices">{t('sidebar.platform_invoices')}</Link>,
      },
      {
        key: 'platform_users',
        icon: <NavIcon icon={faUserShield} />,
        label: <Link to="/platform/users">{t('sidebar.users')}</Link>,
      },
    ];

    if (hasPermission('view_activity_logs')) {
      nav.push({
        key: 'activities',
        icon: <NavIcon icon={faListUl} />,
        label: <Link to="/activities">{t('sidebar.activities')}</Link>,
      });
    }

    return nav;
  }, [t, pendingCount, pendingPlanRequests, supportUnread]);

  const selectedKeys = useMemo(() => {
    const current = location.pathname.replace(/\/$/, '').toLowerCase();
    if (current.startsWith('/dashboard')) return ['dashboard'];
    if (current.startsWith('/vendors')) return ['backoffice_vendors'];
    if (current.startsWith('/subscription-plans')) return ['subscription_plans'];
    if (current.startsWith('/subscriptions')) return ['subscriptions'];
    if (current.startsWith('/plan-requests')) return ['plan_requests'];
    if (current.startsWith('/demo-requests')) return ['demo_requests'];
    if (current.startsWith('/support')) return ['support'];
    if (current.startsWith('/clients')) return ['platform_clients'];
    if (current.startsWith('/orders')) return ['platform_orders'];
    if (current.startsWith('/invoices')) return ['platform_invoices'];
    if (current.startsWith('/platform/users')) return ['platform_users'];
    if (current.startsWith('/activities')) return ['activities'];
    return [];
  }, [location.pathname]);

  return (
    <div className="sidebar-brand-shell">
      <div className="sidebar-brand">
        <Link to="/dashboard" className="sidebar-brand__link">
          {brandLogoUrl ? (
            <img
              src={brandLogoUrl}
              alt={brandAlt}
              className="sidebar-brand__logo"
            />
          ) : (
            <span className="sidebar-brand__fallback">{brandAlt}</span>
          )}
        </Link>
      </div>
      {isAuthenticated() ? (
        <Menu
          theme={isDark ? 'dark' : 'light'}
          mode="inline"
          className={`sidebar-menu ${isDark ? 'sidebar-menu--dark' : 'sidebar-menu--light'}`}
          selectedKeys={selectedKeys}
          items={items}
        />
      ) : null}
    </div>
  );
};

export default SideBar;
